import { useTranslation } from 'react-i18next';

import useAiChatToolLabel from '../../hooks/useAiChatToolLabel';
import useAiChatToolStatus from '../../hooks/useAiChatToolStatus';
import useOpenMonitoringSession from '../../hooks/useOpenMonitoringSession';
import readMonitoringAgentKeys from '../../utils/readMonitoringAgentKeys';
import AiChatToolCard from '../AiChatToolCard';

import type { ReactElement } from 'react';
import type { IAiChatToolCallOf } from '../../interfaces/IAiChatToolCall';

import styles from '../../sass/AiChat.module.scss';

/**
 * The `startMonitoringSession` tool call: a monitoring session started from the chat, one run per
 * unit.
 *
 * The backend made the runs and sends them. When the part arrives live, `useAiChatToolActions`
 * opens the session's results in the run history panel, where the runs move with the socket.
 * This row draws the part and nothing more: the agent, how many units the runs cover with the
 * agent's noun, and a button that opens the same results. It shows no run status and polls
 * nothing. From history it is the same row, and nothing opens.
 */
function StartMonitoringSessionToolCall({ part }: IAiChatToolCallOf<'startMonitoringSession'>): ReactElement {
  const { t } = useTranslation();
  const label = useAiChatToolLabel('startMonitoringSession');
  const status = useAiChatToolStatus(part.state);
  const openMonitoringSession = useOpenMonitoringSession();

  const output = part.state === 'output-available' ? part.output : undefined;

  const readSummary = (): string | undefined => {
    if (!output) {
      return status;
    }

    if (!output.ok) {
      return t('aiChat.tools.agentSessionRefused');
    }

    const { agentKey, unitCountKey } = readMonitoringAgentKeys(output.subType);

    // An action the FE does not know opened the results and nothing else; see `useAiChatToolActions`.
    if ((output.action as string) !== 'monitoringStarted') {
      return t('aiChat.tools.monitoringOpened', { agent: t(agentKey) });
    }

    return t('aiChat.tools.monitoringStarted', {
      units: t(unitCountKey, { count: output.units.length }),
    });
  };

  // Under the header: which session while the call runs, the agent it started, or the reason of a refusal.
  const readNote = (): string | undefined => {
    if (output) {
      return output.ok ? t(readMonitoringAgentKeys(output.subType).agentKey) : output.reason;
    }

    return part.input?.sessionId
      ? t('aiChat.tools.agentSessionFallbackName', { id: part.input.sessionId })
      : undefined;
  };

  const handleOpen = () => {
    if (output?.ok) {
      openMonitoringSession({
        valueChainMapId: output.valueChainMapId,
        sessionId: output.sessionId,
        view: 'results',
        executionKey: output.executionKey,
      });
    }
  };

  const viewButton = output?.ok ? (
    <button type="button" className={styles.toolToggle} onClick={handleOpen}>
      {t('aiChat.tools.monitoringViewResults')}
    </button>
  ) : undefined;

  return (
    <AiChatToolCard
      name={label}
      status={readSummary()}
      content={viewButton}
      errorText={part.errorText}
    >
      {readNote()}
    </AiChatToolCard>
  );
}

export default StartMonitoringSessionToolCall;
